import { useEffect, useState } from "react";
import { MapPin, Clock, ArrowRight, Mail, Phone } from "lucide-react";
import api from "@/lib/api";

// Fallback used only if the API can't be reached — matches the seeded 8 bases.
const FALLBACK = [
  { name: "Fort Belvoir", type: "SUSHI STATION", address: "6000 12th St, Fort Belvoir, VA 22060", hours: "Mon – Sun: 9:00 AM – 6:00 PM ET" },
  { name: "Fort Benning", type: "SUSHI STATION", address: "103 Ingersoll St, Fort Benning, GA 31905", hours: "Mon – Sun: 9:00 AM – 6:00 PM ET" },
];

export default function Locations() {
  const [stores, setStores] = useState(FALLBACK);
  useEffect(() => {
    api.get("/stores").then((r) => {
      if (Array.isArray(r.data) && r.data.length) setStores(r.data);
    }).catch((err) => { console.error("Locations: failed to load stores", err); });
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <p className="eyebrow mb-2">Our Locations</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="locations-title">Find Sandar near you.</h1>
      <p className="text-neutral-600 mt-4 max-w-xl leading-relaxed">8 locations proudly serving our military communities — Sushi Stations and Grab-and-Go, chef-rolled daily.</p>

      <div className="grid sm:grid-cols-2 gap-5 mt-12">
        {stores.map((l) => (
          <div key={l.id || l.name} className="p-6 bg-white rounded-2xl border border-neutral-200/70 card-hover relative" data-testid={`loc-${l.name}`}>
            <div className="flex items-start justify-between">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[hsl(var(--primary))]">
                Military · {l.type}
              </div>
              <span className={`w-2.5 h-2.5 rounded-full mt-1 ${l.is_active === false ? "bg-neutral-300" : "bg-emerald-500"}`} title={l.is_active === false ? "Closed" : "Open"}/>
            </div>
            <h3 className="font-display font-bold text-2xl mt-3">{l.name}</h3>
            <p className="text-sm text-neutral-600 mt-3 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-neutral-400"/>{l.address}</p>
            <p className="text-sm text-neutral-600 mt-2 flex items-center gap-2" data-testid={`loc-hours-${l.name}`}>
              <Clock className="w-4 h-4 text-neutral-400"/>{l.hours || "Mon – Sun: 9:00 AM – 6:00 PM ET"}
            </p>
            <div className="flex items-center gap-6 mt-5 pt-4 border-t border-neutral-100">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.address)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))] hover:gap-2 transition-all"
                data-testid={`view-loc-${l.name}`}
              >View Location <ArrowRight className="w-3.5 h-3.5"/></a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(l.address)}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-neutral-900 hover:text-[hsl(var(--primary))] transition-colors"
              >Directions</a>
            </div>
          </div>
        ))}
      </div>

      {/* Contact strip */}
      <div className="mt-16 grid gap-4 sm:grid-cols-3 p-8 rounded-3xl bg-neutral-950 text-white">
        <a href="tel:7182162013" className="flex items-center gap-3 group" data-testid="contact-phone">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center"><Phone className="w-4 h-4"/></div>
          <div>
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Call us</div>
            <div className="font-display font-semibold group-hover:text-[hsl(var(--primary))] transition-colors">718-216-2013</div>
          </div>
        </a>
        <a href="mailto:info@sandarsushi.com" className="flex items-center gap-3 group" data-testid="contact-info-email">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center"><Mail className="w-4 h-4"/></div>
          <div>
            <div className="text-xs text-neutral-400 uppercase tracking-wider">General</div>
            <div className="font-display font-semibold group-hover:text-[hsl(var(--primary))] transition-colors">info@sandarsushi.com</div>
          </div>
        </a>
        <a href="mailto:htet@sandarsushi.com" className="flex items-center gap-3 group" data-testid="contact-htet-email">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center"><Mail className="w-4 h-4"/></div>
          <div>
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Owner</div>
            <div className="font-display font-semibold group-hover:text-[hsl(var(--primary))] transition-colors">htet@sandarsushi.com</div>
          </div>
        </a>
      </div>
    </div>
  );
}
